/**
 * Push Notification utilities for the PWA.
 * Handles subscription management, permission requests, and browser detection.
 */

/**
 * Check if the browser supports push notifications.
 */
export function isPushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window
}

/**
 * Check if the current device is iOS.
 */
export function isIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream
}

/**
 * Check if the current iOS device supports web push.
 * iOS Safari (tab) does NOT support push.
 * iOS PWA (standalone) on iOS >= 16.4 DOES support push.
 */
export function isIOSPushSupported() {
  if (!isIOS()) return false

  if (!isStandalonePWA()) {
    return false
  }

  const iosVersion = parseIOSVersion()
  return iosVersion >= 16.4
}

/**
 * Returns the category of iOS push capability.
 *
 * - 'not-ios' — not an iOS device
 * - 'not-standalone' — iOS Safari tab (needs "Add to Home Screen")
 * - 'too-old' — iOS standalone but OS version < 16.4 (push not supported)
 * - 'supported' — iOS standalone >= 16.4 (push works)
 */
export function getIOSPushStatus() {
  if (!isIOS()) return 'not-ios'

  if (!isStandalonePWA()) {
    return 'not-standalone'
  }

  const iosVersion = parseIOSVersion()
  if (iosVersion >= 16.4) {
    return 'supported'
  }

  return 'too-old'
}

/**
 * Parse the iOS version from user agent.
 */
function parseIOSVersion() {
  const match = navigator.userAgent.match(/OS (\d+)_(\d+)/)
  if (match) {
    return parseFloat(`${match[1]}.${match[2]}`)
  }
  return 0
}

/**
 * Check if the app is running as a standalone PWA (added to home screen).
 */
export function isStandalonePWA() {
  return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone
}

/**
 * Request notification permission from the browser.
 * Returns 'granted', 'denied', or 'default'.
 */
export async function requestNotificationPermission() {
  if (!('Notification' in window)) {
    return 'unsupported'
  }

  if (Notification.permission === 'granted') {
    return 'granted'
  }

  if (Notification.permission === 'denied') {
    return 'denied'
  }

  const permission = await Notification.requestPermission()
  return permission
}

/**
 * URL-safe Base64 to Uint8Array conversion (required for applicationServerKey).
 */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')

  const rawData = window.atob(base64)
  const outputArray = new Uint8Array(rawData.length)

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i)
  }
  return outputArray
}

/**
 * Subscribe to push notifications.
 * Returns the subscription object, or null on failure.
 */
export async function subscribeToPush(api) {
  try {
    // First get the VAPID public key from the server
    const { publicKey } = await api.push.getVapidPublicKey()

    const registration = await navigator.serviceWorker.ready

    // Check for existing subscription
    let subscription = await registration.pushManager.getSubscription()
    if (subscription) {
      await api.push.subscribe({
        endpoint: subscription.endpoint,
        keys: subscription.toJSON().keys,
        userAgent: navigator.userAgent
      })
      return subscription
    }

    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey)
    })

    // Save to server
    await api.push.subscribe({
      endpoint: subscription.endpoint,
      keys: subscription.toJSON().keys,
      userAgent: navigator.userAgent
    })

    return subscription
  } catch (err) {
    console.error('Failed to subscribe to push:', err)
    return null
  }
}

/**
 * Unsubscribe from push notifications.
 *
 * Order matters: the authenticated server-side DELETE runs FIRST while the auth
 * token is still valid and does not depend on the local unsubscribe result. If
 * the local browser unsubscribe fails afterwards, the server row is already
 * gone; a leftover local subscription is cleaned up on the next launch by
 * syncPushStateOnLaunch (P4).
 */
export async function unsubscribeFromPush(api) {
  let endpoint = null
  try {
    const registration = await navigator.serviceWorker.ready
    const subscription = await registration.pushManager.getSubscription()
    if (!subscription) return

    endpoint = subscription.endpoint
    await api.push.unsubscribe({ endpoint })

    try {
      const unsubscribed = await subscription.unsubscribe()
      if (!unsubscribed) {
        console.warn('pushNotifications: browser unsubscribe() returned false (server row already removed)')
      }
    } catch (localErr) {
      console.warn('pushNotifications: local unsubscribe failed after server removal:', localErr)
    }
  } catch (err) {
    console.error('Failed to unsubscribe from push:', err)
    // Best-effort fallback: if the server call failed but we know the endpoint,
    // retry the authenticated DELETE once (e.g. transient network error).
    if (endpoint) {
      try {
        await api.push.unsubscribe({ endpoint })
      } catch (retryErr) {
        console.error('pushNotifications: server unsubscribe retry failed:', retryErr)
      }
    }
  }
}

/**
 * Get the current notification permission status.
 */
export function getPermissionStatus() {
  if (!('Notification' in window)) {
    return 'unsupported'
  }
  return Notification.permission
}

/**
 * On app launch, verify and self-heal the push subscription (P4).
 * If permission is granted and the account-level push flag is on, make sure a
 * live subscription exists on this device; if the server lost track of it
 * (e.g. inactivity expiry / iOS silent expiry) re-register it silently.
 *
 * Does NOT subscribe when:
 * - The account has push disabled (`pushNotificationsEnabled === false`)
 * - Browser permission is not `granted`
 *
 * The old "not subscribed until the user was asked" rule
 * (`notificationPromptShown`) is intentionally gone: with account on +
 * granted permission the subscription is maintained silently.
 *
 * @param {object} api - API client instance
 * @param {object} store - Vuex store instance
 */
export async function syncPushStateOnLaunch(api, store) {
  if (!isPushSupported()) return

  const permission = getPermissionStatus()
  if (permission !== 'granted') return

  // Respect the account-level opt-in (B1) — never silently re-subscribe
  if (store.state.user?.user?.pushNotificationsEnabled === false) return

  try {
    const registration = await navigator.serviceWorker.ready
    const subscription = await registration.pushManager.getSubscription()

    if (subscription) {
      // Verify with server that our subscription is known
      const { isSubscribed } = await api.push.check({ endpoint: subscription.endpoint })
      if (!isSubscribed) {
        // Re-register — server lost track of this subscription
        await subscribeToPush(api)
      }
    } else {
      // Permission is granted but no active subscription — re-subscribe
      await subscribeToPush(api)
    }
  } catch (err) {
    console.error('Failed to sync push state on launch:', err)
  }
}

/**
 * Re-associate the existing device subscription with the currently signed-in
 * account (P4). After a sign-in on a shared device, the subscription must
 * deliver to the account that just signed in, not to the previous one (B2
 * cleans other accounts' rows for the same endpoint).
 *
 * Never creates a new subscription and never triggers a permission request.
 *
 * @param {object} api - API client instance
 */
export async function reassociateDeviceSubscription(api) {
  if (!isPushSupported() || getPermissionStatus() !== 'granted') return

  try {
    const registration = await navigator.serviceWorker.ready
    const subscription = await registration.pushManager.getSubscription()
    if (!subscription) return

    await api.push.subscribe({
      endpoint: subscription.endpoint,
      keys: subscription.toJSON().keys,
      userAgent: navigator.userAgent
    })
  } catch (err) {
    console.error('Failed to reassociate push subscription:', err)
  }
}
